import { useQuery } from "@tanstack/react-query";
// The bare client, not @/client/libs/axios: that one toasts every failed request, and a failure
// here falls back quietly (see useProductModules).
import _axios from "axios";

import { queryClient } from "@/client/libs/query-client";

/**
 * theResume's menu items that can be hidden from the theRecruit admin console ("theResume Menu").
 * The keys match the ProductModule rows. Dashboard and Settings are not here: they never hide.
 */
export const PRODUCT_MODULE_KEYS = [
  "resumes",
  "spotlight_intro",
  "career_page",
  "interview_requests",
  "practice_interview",
  "feedback_action_hub",
  "job_search",
  "application_status",
  "notifications",
  "career_quest",
] as const;

export type ProductModuleKey = (typeof PRODUCT_MODULE_KEYS)[number];

export type ProductModules = Record<ProductModuleKey, boolean>;

/**
 * Used only when the switches cannot be loaded and none were loaded before: the simplified
 * product. A failure should never reveal what an admin hid.
 */
export const FALLBACK_PRODUCT_MODULES: ProductModules = {
  resumes: true,
  spotlight_intro: false,
  career_page: false,
  interview_requests: false,
  practice_interview: true,
  feedback_action_hub: false,
  job_search: false,
  application_status: false,
  notifications: false,
  career_quest: false,
};

/** Billing features (pricing `Feature.key`) that belong to a hideable module. */
export const BILLING_FEATURE_MODULES: Record<string, ProductModuleKey> = {
  resume_creation: "resumes",
  resume_pdf_downloads: "resumes",
  ai_resume_tailoring: "resumes",
  ats_scoring: "resumes",
  ai_interview_practice: "practice_interview",
  recruiter_interviews: "interview_requests",
  spotlight_intro_videos: "spotlight_intro",
  priority_recruiter_visibility: "job_search",
};

export const PRODUCT_MODULES_KEY = ["product-modules"] as const;

/** A key the server doesn't list yet (code shipped before its row) counts as on. */
export const toProductModules = (raw: Record<string, boolean> | undefined): ProductModules =>
  Object.fromEntries(PRODUCT_MODULE_KEYS.map((key) => [key, raw?.[key] ?? true])) as ProductModules;

export const fetchProductModules = async (): Promise<ProductModules> => {
  const response = await _axios.get<{ modules: Record<string, boolean> }>("/api/product-modules");
  return toProductModules(response.data.modules);
};

const queryOptions = {
  queryKey: PRODUCT_MODULES_KEY,
  queryFn: fetchProductModules,
  // Admin changes reach open tabs within about a minute: when a page mounts or the tab regains
  // focus after the data went stale.
  staleTime: 60_000,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
};

/** For route loaders, so the sidebar renders the right items on the first paint. */
export const prefetchProductModules = () => queryClient.prefetchQuery(queryOptions);

/**
 * `modules` is undefined only while the first load is in flight. After a failed load it is the
 * last good answer, or FALLBACK_PRODUCT_MODULES when there is none.
 */
export const useProductModules = () => {
  const { data, isError } = useQuery(queryOptions);
  const modules = data ?? (isError ? FALLBACK_PRODUCT_MODULES : undefined);

  return {
    modules,
    isEnabled: (key: ProductModuleKey) => modules?.[key] ?? false,
  };
};
